import * as React from 'react';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import { Dropdown, IDropdown, IDropdownOption } from 'office-ui-fabric-react';

import { ContractType, PersonContractViewModel, PersonFunctionViewModel } from '@app/Models';
import { ContractTypeModel } from '@app/Models/Person';
import { PartTimeTzvBlock } from '@app/Pages/PersonInfo/Components/PartTimeTzvBlock';

import { modalService } from '../Modal';
import { TextCell } from './TextCell';
import { ViewMode } from './ViewMode';

type OnChangeContractCallback = (contract: PersonContractViewModel, originalContact?: ContractTypeModel) => Promise<boolean>;

type InlineContractTypeModelDropDownCellProps = {
    typeModel: ContractTypeModel;
    row: PersonContractViewModel;
    fieldKey: keyof PersonContractViewModel;
    onChangeHandler: OnChangeContractCallback;
    isReadOnly?: boolean;
    personFunctions: PersonFunctionViewModel[];
};

@observer
export class InlineContractTypeModelDropDownCell extends React.Component<InlineContractTypeModelDropDownCellProps, {}> {
    private _store: InlineContractModelDropdownCellStore;

    constructor(props: InlineContractTypeModelDropDownCellProps) {
        super(props);
        this._store = new InlineContractModelDropdownCellStore(props);
    }

    componentDidUpdate(prevProps: InlineContractTypeModelDropDownCellProps) {
        if (this.props.row.id !== prevProps.row.id || this.props.typeModel !== prevProps.typeModel
            || this.props.row.type !== prevProps.row.type || this.props.row.partTimePercentage !== prevProps.row.partTimePercentage
            || this.props.row.officePercentage !== prevProps.row.officePercentage || this.props.row.dateFrom !== prevProps.row.dateFrom ||
            this.props.row.dateTo !== prevProps.row.dateTo || this.props.personFunctions !== prevProps.personFunctions) {
            this._store.updateProps(this.props);
        }
    }

    render() {
        if (this._store.mode === ViewMode.view) {
            return <TextCell onClick={this._store.toEditMode} value={this._store.typeModel as string}/>;
        } else {
            return (
                <Dropdown
                    options={this._store.contractTypeModelOptions}
                    onChange={this._store.onChange}
                    componentRef={this._store.dropdownRef}
                    onDismiss={this._store.onMouseLeave}
                    selectedKey={this._store.typeModel as string}
                />
            );
        }
    }
}

class InlineContractModelDropdownCellStore {
    @observable mode: string = ViewMode.view;
    @observable typeModel: ContractTypeModel;
    @observable _initTypeModel: ContractTypeModel;
    dropdownRef: React.RefObject<IDropdown> = React.createRef();
    private _currentProps: InlineContractTypeModelDropDownCellProps;

    constructor(props: InlineContractTypeModelDropDownCellProps) {
        this.updateProps(props);
    }

    @action.bound
    async toEditMode() {
        if (this._currentProps.row.type === ContractType.Tzv) {
            this.mode = ViewMode.view;
            if (!this._currentProps.personFunctions) {
                return;
            }

            await modalService.show(PartTimeTzvBlock, {
                contract: this._currentProps.row,
                personFunctions: this._currentProps.personFunctions,
                onSave: (c) => {
                    return this._currentProps.onChangeHandler(c, this._initTypeModel);
                },
            });
        } else {
            if (this._currentProps.isReadOnly) return;
            this.mode = ViewMode.edit;
            setTimeout(() => {
                this.dropdownRef.current?.focus(true);
            }, 0);
        }
    }

    @action
    onMouseLeave = () => {
        this.mode = ViewMode.view;
    };

    @action
    onChange = async (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        this.mode = ViewMode.view;
        const currentModel = this.typeModel;
        
        const row = this._currentProps.row as unknown as { [key: string]: string };
        if (option) {
            this.typeModel = option.key as ContractTypeModel;
            row[this._currentProps.fieldKey] = this.typeModel as string;
        }

        if (await this._currentProps.onChangeHandler(this._currentProps.row, this._initTypeModel)){
            this._initTypeModel = this.typeModel;
        } else {
            this.typeModel = currentModel;
            row[this._currentProps.fieldKey] = currentModel as string;
        }
    };


    get contractTypeModelOptions() {
        const typeIndex = this._currentProps.row.type;
        let keys = [];
        if (typeIndex === ContractType.Tzv) {
            keys.push({ key: ContractTypeModel.TZV_Month, text: ContractTypeModel.TZV_Month });
            keys.push({ key: ContractTypeModel.TZV_Day, text: ContractTypeModel.TZV_Day });
            keys.push({ key: ContractTypeModel.TZV_VariableBlock, text: ContractTypeModel.TZV_VariableBlock });
            keys.push({ key: ContractTypeModel.TZV_VariableDay, text: ContractTypeModel.TZV_VariableDay });
            keys.push({ key: ContractTypeModel.TZV_FixBlock, text: ContractTypeModel.TZV_FixBlock });
            keys.push({ key: ContractTypeModel.TZV_FixDay, text: ContractTypeModel.TZV_FixDay });
        } else if (typeIndex === ContractType.Freelance) {
            keys.push({ key: ContractTypeModel.None, text: ContractTypeModel.None });
            keys.push({ key: ContractTypeModel.FL_A, text: ContractTypeModel.FL_A });
            keys.push({ key: ContractTypeModel.FL_B, text: ContractTypeModel.FL_B });
            keys.push({ key: ContractTypeModel.FL_C, text: ContractTypeModel.FL_C });
            keys.push({ key: ContractTypeModel.FL_S, text: ContractTypeModel.FL_S });
        } else {
            keys.push({ key: ContractTypeModel.None, text: ContractTypeModel.None });
        }
        return keys;
    }

    @action
    updateProps(props: InlineContractTypeModelDropDownCellProps) {
        this._initTypeModel = props.typeModel;
        this._currentProps = { ...props };
        this.typeModel = props.typeModel;
    }
}